import {
  Box,
  Fade,
  InputAdornment,
  Popper,
  SvgIcon,
  ClickAwayListener,
  IconButton,
} from '@mui/material';
import { useState, FunctionComponent } from 'react';
import { DateRange, RangeKeyDict } from 'react-date-range';
import { Controller, useFormContext } from 'react-hook-form';

import TextField from '@Components/TextField';
import Icons from '@Constants/Icons.constants';
import { ReactHookFormError } from '@Typings/global.types';
import { getDateMMDDYYYY } from '@Utils/common/common.utils';

import { DateRangePickerControllerProps } from './DateRangePickerController.types';

import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import '@Components/DateRangePicker/DateRangePicker.styles.scss'; // custom css

const DateRangePickerController: FunctionComponent<DateRangePickerControllerProps> =
  (props: DateRangePickerControllerProps): JSX.Element => {
    const {
      dateState,
      getDateState,
      setDateState,
      disabled,
      maxDate,
      size,
      placeholder,
      label,
      name,
    } = props;

    const {
      control,
      formState: { errors },
    } = useFormContext();

    /** STATES */
    const [open, setOpen] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    /** HANDLES CLICK ON DATE RANGE TEXTFIELD */
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setOpen((previousOpen) => !previousOpen);
    };

    /** HANDLES CLICK OUTSIDE OF DATE RANGE (closes the dropdown) */
    const handleClickAway = () => {
      setOpen(false);
    };

    /** ON DATE RANGE SELECT */
    const onChange = (range: RangeKeyDict) => {
      if (range && range.selection) {
        const { startDate, endDate } = range.selection;
        setDateState(range.selection);

        if (getDateState) {
          getDateState(startDate, endDate);
        }
      }
    };

    /** HANDLES CLEAR ICON CLICK */
    const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      handleClickAway();
      setDateState({
        startDate: undefined,
        endDate: undefined,
        key: 'selection',
      });

      if (getDateState) {
        getDateState(undefined, undefined);
      }
    };

    /** Date With Format (mm/dd/yyyy) */
    const formattedDate = (): string => {
      if (dateState.startDate?.getDate() && dateState.endDate?.getDate()) {
        if (dateState.startDate?.getDate() === dateState.endDate?.getDate()) {
          return `${getDateMMDDYYYY(dateState.startDate)}`;
        }
        return `${getDateMMDDYYYY(
          dateState.startDate
        )} \u2013 ${getDateMMDDYYYY(dateState.endDate)}`;
      }
      return '';
    };

    return (
      <>
        <Controller
          name={name}
          control={control}
          render={() => (
            <ClickAwayListener onClickAway={handleClickAway}>
              <Box>
                <TextField
                  focused={open}
                  disabled={disabled}
                  onClick={!disabled ? handleClick : () => {}}
                  placeholder={placeholder}
                  value={formattedDate()}
                  size={size}
                  label={label}
                  fullWidth
                  sx={{
                    '&:hover': {
                      '& .clearBtn': {
                        display: 'flex',
                      },
                    },
                    '& .MuiInputBase-input': {
                      pr: 0,
                    },
                    '& .MuiOutlinedInput-root, & .MuiOutlinedInput-input': {
                      cursor: !disabled ? 'pointer' : 'not-allowed',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{
                          m: 0,
                          cursor: !disabled ? 'pointer' : 'not-allowed',
                        }}
                      >
                        <Box minWidth="3.6rem">
                          {(dateState.startDate || dateState.endDate) && (
                            <IconButton
                              className="clearBtn"
                              sx={{
                                display: 'none',
                                m: 0,
                                p: 0.4,
                                ml: 0.4,
                              }}
                              onClick={handleClear}
                            >
                              <SvgIcon>{Icons.close}</SvgIcon>
                            </IconButton>
                          )}
                        </Box>
                        <SvgIcon
                          sx={{
                            mr: 1.2,
                            ml: 0.8,
                            color: !disabled
                              ? ({ colors }) =>
                                  open ? 'secondary.main' : colors.inputText
                              : 'action.disabled',
                          }}
                        >
                          {Icons.dateRange}
                        </SvgIcon>
                      </InputAdornment>
                    ),
                    inputProps: {
                      disabled: true,
                    },
                  }}
                  error={!!errors[name]}
                  helperText={
                    (errors[name] as unknown as ReactHookFormError)?.message
                  }
                />
                <Popper
                  open={open}
                  anchorEl={anchorEl}
                  transition
                  disablePortal
                  style={{ zIndex: 100 }}
                >
                  {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={350}>
                      <Box>
                        <DateRange
                          editableDateInputs
                          onChange={onChange}
                          moveRangeOnFirstSelection={false}
                          ranges={[dateState]}
                          showDateDisplay={false}
                          maxDate={maxDate}
                        />
                      </Box>
                    </Fade>
                  )}
                </Popper>
              </Box>
            </ClickAwayListener>
          )}
        />
      </>
    );
  };

export default DateRangePickerController;
